import React from "react";
import { AmorgAdminToken, ArticleNote, fieldValidation, rng } from "../../../../../amorg-common";
import { articlesClientApi } from "../../../../../api/articlesClientApi";
import { Button } from "../../../../../designSystem/button/Button";
import { Card } from "../../../../../designSystem/Card";
import { AnimatedCollapsibleContent } from "../../../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { DeleteConfirmationButton } from "../../../../../designSystem/compositeComponents/DeleteConfirmationButton";
import { Flex } from "../../../../../designSystem/compositeComponents/FlexHelpers";
import { Divider } from "../../../../../designSystem/Divider";
import { InputMessage } from "../../../../../designSystem/InputMessage";
import { CountAvatar } from "../../../../../designSystem/avatar/CountAvatar";
import { TextArea } from "../../../../../designSystem/inputs/TextArea";
import { Modal } from "../../../../../designSystem/Modal";
import { H3 } from "../../../../../designSystem/typography/Heading";
import { toDateTimeString } from "../../../../../utils/date";

type ArticleViewNotesPanelProps = {
    activeUser: AmorgAdminToken;
    articleVersionId: string,
    notes: ArticleNote[],
    onChanged: () => void;
};
export function ArticleViewNotesPanel (props: ArticleViewNotesPanelProps): JSX.Element
{
    const [showNotes, setShowNotes] = React.useState<boolean>(false);
    const [newNote, setNewNote] = React.useState('');
    const [newNoteError, setNewNoteError] = React.useState('');

    async function handleClickAddNote ()
    {
        if (!fieldValidation.articleNote.note(newNote))
        {
            if (newNote.length === 0)
                setNewNoteError('Noten skal udfyldes');
            else
                setNewNoteError(`Noten skal udfyldes. Maks. 5000 tegn (${newNote.length})`);

            return;
        }

        await articlesClientApi.createNote(
            props.articleVersionId,
            rng.getRandomId(),
            newNote
        );
        setNewNoteError('');
        setNewNote('');

        props.onChanged();
    }

    async function handleClickDelete (id: string)
    {
        await articlesClientApi.deleteNote(id);

        props.onChanged();
    }

    return (
        <Flex flexDirection="column">
            <Flex>
                <Flex gap={8} style={{ flexGrow: 1 }}>
                    <H3>Interne noter</H3>
                    {props.notes.length > 0 &&
                        <CountAvatar count={props.notes.length} size='24px' />
                    }
                </Flex>
                <Button variant='text' icon={showNotes ? 'caret_caretUp' : 'caret_caretDown'} onClick={() => setShowNotes(!showNotes)} />
            </Flex>
            <AnimatedCollapsibleContent open={showNotes}>

                <Flex flexDirection='column' gap={32}>
                    <Divider style={{ marginTop: '10px' }} />
                    <Flex flexDirection="column" gap={16}>
                        {props.notes.length === 0 &&
                            <div>Der er ingen noter</div>
                        }
                        {props.notes.map((note) =>
                        {
                            const isAuthor = props.activeUser.userId === note.user.userId;
                            return (
                                <Flex key={note.id} justifyContent={isAuthor ? 'flex-start' : 'flex-end'}>
                                    <Flex flexDirection='column' gap={8} style={{ width: '80%' }}>
                                        <Card variant="outlined">
                                            <Flex gap={8} alignItems='center'>
                                                <b>{note.user.email}</b>
                                                {note.lastEdited !== note.created
                                                    ? <small>{toDateTimeString(note.created)} (redigeret {toDateTimeString(note.lastEdited)})</small>
                                                    : <small>{toDateTimeString(note.created)}</small>
                                                }
                                                {props.activeUser.userId === note.user.userId &&
                                                    <EditNoteModalButton note={note} onChanged={props.onChanged} />
                                                }
                                                {props.activeUser.userId === note.user.userId &&
                                                    <DeleteConfirmationButton
                                                        variant='text'
                                                        buttonText=""
                                                        asIcon
                                                        confirmationHeading="Slet note"
                                                        confirmationText="Er du sikker på at du vil slette denne note?"
                                                        requireKey={false}
                                                        onConfirmDelete={() => handleClickDelete(note.id)}
                                                    />
                                                }
                                            </Flex>
                                            <div>{note.note}</div>
                                        </Card>
                                    </Flex>
                                </Flex>
                            );
                        })}
                    </Flex>

                    <Flex flexDirection='column' gap={16}>
                        <TextArea inErrorState={newNoteError !== ''} placeholder='Skriv en ny note...' value={newNote} onChange={(value) => setNewNote(value)} />
                        <Flex justifyContent='flex-end'>
                            {newNoteError !== '' &&
                                <div style={{ flexGrow: 1 }}>
                                    <InputMessage inErrorState={newNoteError !== ''}>
                                        {newNoteError}
                                    </InputMessage>
                                </div>
                            }
                            <div>
                                <Button variant='outlined' text='Tilføj note' onClick={handleClickAddNote} />
                            </div>
                        </Flex>
                    </Flex>

                </Flex>
            </AnimatedCollapsibleContent>
        </Flex>
    );
}

type EditNoteModalButtonProps = {
    note: ArticleNote,
    onChanged: () => void;
};
function EditNoteModalButton (props: EditNoteModalButtonProps): JSX.Element
{
    const [show, setShow] = React.useState<boolean>(false);
    const [note, setNote] = React.useState('');
    const [noteError, setNoteError] = React.useState('');

    function handleClickShow ()
    {
        setNote(props.note.note);
        setNoteError('');
        setShow(true);
    }

    async function handleClickUpdateNote ()
    {
        if (!fieldValidation.articleNote.note(note))
        {
            if (note.length === 0)
                setNoteError('Noten skal udfyldes');
            else
                setNoteError(`Noten skal udfyldes. Maks. 5000 tegn (${note.length})`);

            return;
        }

        await articlesClientApi.updateNote(
            props.note.id,
            note
        );
        setNoteError('');
        setNote('');
        setShow(false);

        props.onChanged();
    }

    return (
        <>
            <Button variant='text' icon='interface_actions_edit' onClick={handleClickShow} />
            <Modal title='Rediger note' showModal={show} onCancel={() => setShow(false)}
                content={
                    <>
                        <TextArea inErrorState={noteError !== ''} placeholder='Skriv en ny note...' value={note} onChange={(value) => setNote(value)} />
                        {noteError !== '' &&
                            <InputMessage inErrorState={noteError !== ''}>
                                {noteError}
                            </InputMessage>
                        }

                        <Button variant='outlined' text='Opdater note' onClick={handleClickUpdateNote} />
                    </>
                } />
        </>
    );
}